import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Dimensions,
  Alert,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';
import { Ionicons } from '@expo/vector-icons';
import { UserService, CheckInService, MacroService, WorkoutService, AuthService } from '../../services/ApiService';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const chartWidth = SCREEN_WIDTH - 64; // Container margins (16*2) + padding (16*2)

const DashboardScreen = () => {
  const insets = useSafeAreaInsets();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    weight: [],
    macros: {},
    workouts: {},
    recentWorkouts: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Calculate date range for consistency chart
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      const [userData, macroStats, workoutStats, latestWeightData, checkinHistory, planData, workoutHistory] = await Promise.all([
        UserService.getProfile(),
        MacroService.getMacroStats(7),
        WorkoutService.getWorkoutStats(30),
        CheckInService.getLatestWeight(),
        CheckInService.getCheckInHistory(1, 30),
        UserService.getPlanVersionsAndCheckIn(),
        WorkoutService.getWorkoutHistory(sevenDaysAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]),
      ]);

      setUser(userData);
      setStats({
        macros: macroStats,
        workouts: workoutStats,
        weight: latestWeightData,
        checkins: checkinHistory.checkIns || [],
        recentWorkouts: workoutHistory.workouts || [],
      });
    } catch (error) {
      console.error('Dashboard data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0, visible: false, value: 0, label: '' });

  const onRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    console.log('DashboardScreen: Logout button pressed');
    try {
      console.log('DashboardScreen: Starting logout process...');
      await AuthService.logout();
      console.log('DashboardScreen: Logout successful - user will be redirected to login');
      // Navigation will be handled by the auth state change in App.js
    } catch (error) {
      console.error('DashboardScreen: Logout error:', error);
      Alert.alert(
        'Logout Failed',
        'Failed to logout. Please try again.\n\nIf the problem persists, please restart the app.',
        [
          { text: 'OK', style: 'default' }
        ]
      );
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getPlanTypeColor = (planType) => {
    switch (planType) {
      case 'basic': return '#FF6B6B';
      case 'pro': return '#10B981';
      case 'human': return '#45B7D1';
      default: return '#95A5A6';
    }
  };

  const getPlanTypeLabel = (planType) => {
    switch (planType) {
      case 'basic': return 'Basic AI';
      case 'pro': return 'Pro AI';
      case 'human': return 'Human Trainer';
      default: return 'Unknown';
    }
  };

  const prepareWeightChartData = () => {
    const labels = [];
    const weights = [];

    // 1. Initial weight from onboarding (Start)
    if (user && user.weight) {
      labels.push('Start');
      weights.push(user.weight);
    }

    // 2. All check-in weights
    if (stats.checkins && stats.checkins.length > 0) {
      const sortedCheckins = [...stats.checkins].sort((a, b) => new Date(a.date) - new Date(b.date));
      
      sortedCheckins.forEach(checkin => {
        const date = new Date(checkin.date);
        labels.push(date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }));
        weights.push(checkin.weight);
      });
    }

    // 3. Add latest/current weight conditionally
    const lastChartWeight = weights.length > 0 ? weights[weights.length - 1] : 0;
    const currentWeight = stats.weight?.weight || user?.weight || 0;

    // Only add 'Today' if either it's the only data point, or the current weight is meaningfully different 
    // from the very last point in the chart (e.g., they just updated profile weight independently of check-in)
    // AND if there's checkin history. If there's no checkins, we already have 'Start'.
    if (weights.length === 0) {
      labels.push('Today');
      weights.push(currentWeight > 0 ? currentWeight : 0);
    } else if (Math.abs(currentWeight - lastChartWeight) > 0.1) {
      // Make sure we don't accidentally duplicate if date hasn't shifted much
      labels.push('Now');
      weights.push(currentWeight);
    }

    // Optional safety against too many points crushing the chart component
    let finalLabels = labels;
    let finalWeights = weights;
    
    // LineChart crashes/overlaps text wildly if there are way too many points. 
    // But to keep it "completely accurate", we should at least cap it at ~15-20 points optimally.
    if (finalLabels.length > 20) {
      // we take the first ('Start'), and the last 19
      finalLabels = [labels[0], ...labels.slice(-(19))];
      finalWeights = [weights[0], ...weights.slice(-(19))];
    }

    return {
      labels: finalLabels,
      datasets: [{
        data: finalWeights,
        color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`, // Blue color
        strokeWidth: 3,
      }],
    };
  };

  const handleDataPointClick = (data) => {
    const { index, value, x, y } = data;
    const chartData = prepareWeightChartData();
    const date = chartData.labels[index];
    const weight = value;

    setTooltip({
      date: date,
      weight: weight.toFixed(1),
      x: x,
      y: y,
    });
  };

  const getLast7Days = () => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d);
    }
    return dates;
  };

  const prepareWorkoutConsistencyData = () => {
    const last7Days = getLast7Days();
    const labels = last7Days.map(d => {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days[d.getDay()];
    });

    // Check intersection of last 7 days with workout logs
    const data = last7Days.map(day => {
      const dayStr = day.toISOString().split('T')[0];
      const hasWorkout = stats.recentWorkouts.some(w => {
        // Handle potentially different date field names or formats
        const wDate = w.date || w.createdAt || '';
        return wDate.startsWith(dayStr);
      });
      return hasWorkout ? 1 : 0;
    });

    return {
      labels,
      datasets: [{ data }]
    };
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        <View style={styles.loadingContainer}>
          <Text style={{ color: '#FFFFFF' }}>Loading dashboard...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <View style={[styles.safeArea, { paddingTop: insets.top }]}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={[styles.header, { paddingTop: 0 }]}>
          <View>
            <Text style={styles.greeting}>{getGreeting()},</Text>
            <Text style={styles.userName}>{user?.name || 'User'}</Text>
          </View>
          <View style={styles.headerRight}>
            <View style={styles.planBadge}>
              <Text style={[styles.planText, { color: getPlanTypeColor(user?.planType) }]}>
                {getPlanTypeLabel(user?.planType)}
              </Text>
            </View>
            <TouchableOpacity
              style={styles.logoutButton}
              onPress={handleLogout}
            >
              <Ionicons name="log-out-outline" size={24} color="#FF3B30" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Quick Stats */}
        <View style={styles.statsContainer}>
          <View style={styles.statCard}>
            <Ionicons name="fitness-outline" size={24} color="#3B82F6" />
            <Text style={styles.statValue}>{stats.weight?.weight || user?.weight || 0} kg</Text>
            <Text style={styles.statLabel}>
              {stats.weight?.source === 'checkin' ? 'Latest Weight' : 'Current Weight'}
            </Text>
            {stats.weight?.date && (
              <Text style={styles.statSubtext}>
                {new Date(stats.weight.date).toLocaleDateString('en-GB')}
              </Text>
            )}
          </View>

          <View style={styles.statCard}>
            <Ionicons name="checkmark-circle-outline" size={24} color="#FACC15" />
            <Text style={styles.statValue}>{stats.macros.adherenceRate || 0}%</Text>
            <Text style={styles.statLabel}>Macro Adherence</Text>
          </View>
        </View>

        {/* Weight Progress Chart */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Weight Progress (30 days)</Text>
          <View style={{ position: 'relative' }}>
            <LineChart
              data={prepareWeightChartData()}
              width={chartWidth}
              height={160}
              chartConfig={{
                backgroundColor: '#161C2D',
                backgroundGradientFrom: '#ffffff',
                backgroundGradientTo: '#ffffff',
                decimalPlaces: 1,
                color: (opacity = 1) => `rgba(99, 102, 241, ${opacity})`, // Modern indigo color
                labelColor: (opacity = 1) => `rgba(31, 41, 55, ${opacity})`,
                style: {
                  borderRadius: 16,
                },
                propsForDots: {
                  r: '6',
                  strokeWidth: '2',
                  stroke: '#3B82F6', // Modern indigo stroke
                  fill: '#3B82F6', // Modern indigo fill
                },
              }}
              bezier
              style={styles.chart}
              onDataPointClick={({ x, y, value, index }) => {
                const data = prepareWeightChartData();
                const isSamePoint = (tooltipPos.x === x && tooltipPos.y === y);
                
                if (isSamePoint) {
                  setTooltipPos(prev => ({ ...prev, visible: !prev.visible }));
                } else {
                  setTooltipPos({ 
                    x, 
                    y, 
                    value, 
                    label: data.labels[index], 
                    visible: true 
                  });
                }
              }}
            />
            {tooltipPos.visible && (
              <View style={{
                position: 'absolute',
                top: tooltipPos.y - 45,
                left: tooltipPos.x - 35,
                backgroundColor: '#161C2D',
                padding: 6,
                borderRadius: 8,
                alignItems: 'center',
                shadowColor: '#000',
                shadowOpacity: 0.2,
                shadowRadius: 4,
                elevation: 4,
                minWidth: 70,
                zIndex: 100,
              }}>
                <Text style={{ color: '#ffffff', fontWeight: 'bold', fontSize: 12 }}>{tooltipPos.value} kg</Text>
                <Text style={{ color: '#6D759E', fontSize: 10 }}>{tooltipPos.label}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Workout Consistency (Last 7 Days) */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Workout Consistency (Last 7 Days)</Text>
          <BarChart
            data={prepareWorkoutConsistencyData()}
            width={chartWidth}
            height={160}
            yAxisLabel=""
            yAxisSuffix=""
            chartConfig={{
              backgroundColor: '#161C2D',
              backgroundGradientFrom: '#ffffff',
              backgroundGradientTo: '#ffffff',
              decimalPlaces: 0,
              color: (opacity = 1) => `rgba(34, 197, 94, ${opacity})`, // Green
              labelColor: (opacity = 1) => `rgba(31, 41, 55, ${opacity})`,
              barPercentage: 0.7,
            }}
            style={styles.chart}
            fromZero={true}
            showValuesOnTopOfBars={false}
            withInnerLines={false}
          />
        </View>

        {/* Macro Breakdown (Pie Chart) */}
        <View style={styles.chartContainer}>
          <Text style={styles.chartTitle}>Average Calorie Split (7 days)</Text>

          <View style={{ alignItems: 'center', justifyContent: 'center' }}>
            {/* Show Avg Calories as Text Context */}
            <Text style={{ fontSize: 14, color: '#6D759E', marginBottom: 10 }}>
              Avg. Intake: <Text style={{ fontWeight: 'bold', color: '#FFFFFF' }}>{stats.macros.averageCalories || 0} kcal</Text>
            </Text>

            <PieChart
              data={[
                {
                  name: 'Protein',
                  population: (stats.macros.averageProtein || 0) * 4,
                  color: '#3b82f6', // Blue
                  legendFontcolor: '#FFFFFF',
                  legendFontSize: 12,
                },
                {
                  name: 'Carbs',
                  population: (stats.macros.averageCarbs || 0) * 4,
                  color: '#10b981', // Green
                  legendFontcolor: '#FFFFFF',
                  legendFontSize: 12,
                },
                {
                  name: 'Fats',
                  population: (stats.macros.averageFats || 0) * 9,
                  color: '#FACC15', // Orange
                  legendFontcolor: '#FFFFFF',
                  legendFontSize: 12,
                },
              ]}
              width={chartWidth}
              height={200}
              chartConfig={{
                color: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
              }}
              accessor="population"
              backgroundColor="transparent"
              paddingLeft="15"
              absolute={false}
            />
          </View>
        </View>


        {/* Recent Activity */}
        {/* <View style={styles.activityContainer}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        <View style={styles.activityItem}>
          <Ionicons name="checkmark-circle" size={20} color="#3B82F6" />
          <Text style={styles.activityText}>Completed workout - Chest & Triceps</Text>
          <Text style={styles.activityTime}>2 hours ago</Text>
        </View>
        <View style={styles.activityItem}>
          <Ionicons name="restaurant" size={20} color="#FACC15" />
          <Text style={styles.activityText}>Logged breakfast - Oatmeal</Text>
          <Text style={styles.activityTime}>4 hours ago</Text>
        </View>
        <View style={styles.activityItem}>
          <Ionicons name="chatbubble" size={20} color="#3B82F6" />
          <Text style={styles.activityText}>AI chat - Asked about protein intake</Text>
          <Text style={styles.activityTime}>1 day ago</Text>
        </View>
      </View> */}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  scrollContent: {
    paddingBottom: Math.max(20, SCREEN_HEIGHT * 0.03),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: SCREEN_HEIGHT * 0.7,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.04),
    paddingBottom: 16,
    backgroundColor: '#161C2D',
    borderBottomWidth: 1,
    borderBottomColor: '#2C365E',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 80,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Math.max(8, SCREEN_WIDTH * 0.02),
    flexShrink: 1,
  },
  logoutButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#0B0E14',
    minWidth: 40,
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  greeting: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.04),
    color: '#6D759E',
    marginBottom: 2,
  },
  userName: {
    fontSize: Math.max(20, SCREEN_WIDTH * 0.06),
    fontWeight: 'bold',
    color: '#FFFFFF',
    flexShrink: 1,
  },
  planBadge: {
    backgroundColor: '#0B0E14',
    paddingHorizontal: Math.max(10, SCREEN_WIDTH * 0.03),
    paddingVertical: Math.max(4, SCREEN_HEIGHT * 0.005),
    borderRadius: 20,
    marginRight: 4,
  },
  planText: {
    fontSize: Math.max(10, SCREEN_WIDTH * 0.03),
    fontWeight: '600',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: Math.max(12, SCREEN_WIDTH * 0.04),
    paddingTop: Math.max(16, SCREEN_HEIGHT * 0.02),
    // paddingBottom: Math.max(8, SCREEN_HEIGHT * 0.01),
  },
  statCard: {
    flex: 1,
    backgroundColor: '#161C2D',
    padding: Math.max(10, SCREEN_WIDTH * 0.03),
    borderRadius: 16,
    alignItems: 'center',
    marginHorizontal: Math.max(3, SCREEN_WIDTH * 0.008),
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#f1f5f9',
    minHeight: 100,
  },
  statValue: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.045),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: Math.max(6, SCREEN_HEIGHT * 0.008),
    textAlign: 'center',
  },
  statLabel: {
    fontSize: Math.max(10, SCREEN_WIDTH * 0.03),
    color: '#6D759E',
    marginTop: 4,
    textAlign: 'center',
  },
  statSubtext: {
    fontSize: Math.max(9, SCREEN_WIDTH * 0.025),
    color: '#6D759E',
    marginTop: 2,
    textAlign: 'center',
  },
  chartContainer: {
    backgroundColor: '#161C2D',
    marginHorizontal: Math.max(12, SCREEN_WIDTH * 0.04),
    marginTop: Math.max(8, SCREEN_HEIGHT * 0.01),
    marginBottom: Math.max(8, SCREEN_HEIGHT * 0.01),
    padding: Math.max(14, SCREEN_WIDTH * 0.04),
    borderRadius: 20,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 1,
    borderColor: '#f1f5f9',
  },
  chartTitle: {
    fontSize: Math.max(16, SCREEN_WIDTH * 0.045),
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: Math.max(12, SCREEN_HEIGHT * 0.015),
    letterSpacing: -0.5,
  },
  chartWrapper: {
    position: 'relative',
  },
  chart: {
    borderRadius: 16,
    marginHorizontal: -4, // Slight adjustment to ensure perfect fit
  },
  tooltip: {
    position: 'absolute',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 100,
  },
  tooltipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
    textAlign: 'center',
  },
  tooltipWeight: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
    textAlign: 'center',
    marginTop: 2,
  },
  activityContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 10,
    padding: 16,
    borderRadius: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  activityText: {
    flex: 1,
    fontSize: 14,
    color: '#FFFFFF',
    marginLeft: 12,
  },
  activityTime: {
    fontSize: 12,
    color: '#6D759E',
  },
});

export default DashboardScreen;
